import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  UserPlus, Mail, Phone, Award, MapPin, Clock, 
  CheckCircle, XCircle, Copy, RefreshCw 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ApplicationManagement = () => {
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/roadside/admin/applications`);
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const handleApprove = async (applicationId) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/roadside/admin/approve-application?application_id=${applicationId}`
      );
      
      toast.success('Application approved!', {
        description: 'Signup link generated successfully'
      });

      // Copy signup URL to clipboard
      if (response.data.signup_url) {
        navigator.clipboard.writeText(response.data.signup_url);
        toast.success('Signup link copied to clipboard!');
      }

      await fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      toast.error('Failed to approve application', {
        description: error.response?.data?.detail
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async (applicationId) => {
    if (!window.confirm('Are you sure you want to reject this application?')) return;

    setIsLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/roadside/admin/reject-application?application_id=${applicationId}&reason=Not qualified`
      );
      
      toast.success('Application rejected');
      await fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      toast.error('Failed to reject application');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'Approved': 'bg-green-100 text-green-800 border-green-300',
      'Rejected': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const pendingApps = applications.filter(app => app.status === 'Pending');
  const reviewedApps = applications.filter(app => app.status !== 'Pending');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Provider Applications</h2>
          <p className="text-sm text-gray-600 mt-1">
            {pendingApps.length} pending • {applications.length} total
          </p>
        </div>
        <Button onClick={fetchApplications} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Pending Applications */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          Pending Review ({pendingApps.length})
        </h3>
        
        {pendingApps.length === 0 ? (
          <Card className="p-8 text-center">
            <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No pending applications</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {pendingApps.map((app) => (
              <Card
                key={app.application_id}
                className={`p-4 cursor-pointer transition-all ${
                  selectedApp?.application_id === app.application_id
                    ? 'border-2 border-blue-500 bg-blue-50'
                    : 'border hover:border-gray-300'
                }`}
                onClick={() => setSelectedApp(app)}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">{app.name}</h4>
                    <p className="text-sm text-gray-600">#{app.application_id}</p>
                  </div>
                  <Badge className={`${getStatusColor(app.status)} border`}>
                    {app.status}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                  <div className="flex items-center text-gray-600">
                    <Mail className="w-4 h-4 mr-2" />
                    {app.email}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Phone className="w-4 h-4 mr-2" />
                    {app.phone}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Award className="w-4 h-4 mr-2" />
                    {app.experience_years} years experience
                  </div>
                  <div className="flex items-center text-gray-600">
                    <MapPin className="w-4 h-4 mr-2" />
                    {app.service_area}
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedApp?.application_id === app.application_id && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-1">Certifications:</p>
                      <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                        {app.certifications}
                      </p>
                    </div>
                    
                    {app.additional_info && (
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-1">Additional Info:</p>
                        <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                          {app.additional_info}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center text-xs text-gray-500">
                      <Clock className="w-3 h-3 mr-1" />
                      Applied: {new Date(app.created_at).toLocaleDateString()} at{' '}
                      {new Date(app.created_at).toLocaleTimeString()}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        onClick={() => handleApprove(app.application_id)}
                        disabled={isLoading}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve & Generate Link
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(app.application_id)}
                        disabled={isLoading}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Reviewed Applications */}
      {reviewedApps.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Reviewed Applications ({reviewedApps.length})
          </h3>
          
          <div className="space-y-3">
            {reviewedApps.map((app) => (
              <Card key={app.application_id} className="p-4 bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-bold text-gray-900">{app.name}</h4>
                    <p className="text-sm text-gray-600">{app.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Reviewed: {app.reviewed_at ? new Date(app.reviewed_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <Badge className={`${getStatusColor(app.status)} border`}>
                    {app.status}
                  </Badge>
                </div>

                {app.status === 'Approved' && app.signup_token && (
                  <div className="mt-3 flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const url = `${window.location.origin}/provider/signup?token=${app.signup_token}`;
                        navigator.clipboard.writeText(url);
                        toast.success('Signup link copied!');
                      }}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy Signup Link
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
